import { useEffect, useState } from 'react';

import {
  crosshairLayout,
  dataPointLayout,
} from './CategoricalBarChartInspectOverlay.sty.js';
import { plotLayout } from '../../../../core/components/chart-grid/ChartGrid.sty.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { HorizontalCrosshair } from '../../../../core/components/crosshair/HorizontalCrosshair.js';
import { getMagnifierLabel } from '../../../../core/components/crosshair/utils/get-magnifier-label.js';
import { VerticalCrosshair } from '../../../../core/components/crosshair/VerticalCrosshair.js';
import { THRESHOLD_BAR_THICKNESS } from '../../../../core/components/threshold-bar/types/constants.js';
import { useApplyCallbackOnScroll } from '../../../../core/hooks/useApplyCallbackOnScroll.js';
import { GroupDataPoint } from '../../../../core/types/data-point.js';
import {
  useMouseInBoundsState,
  useMousePositionState,
  useSetState,
  useTooltipState,
} from '../../../hooks/store/selectors.js';
import { setMouseOutBounds } from '../../../hooks/store/state-setters.js';
import { useCategoricalBarChartBaseConfig } from '../../../hooks/useCategoricalBarChartBaseConfig.js';
import { useCategoricalBarChartData } from '../../../hooks/useCategoricalBarChartData.js';
import { useCategoricalBarChartOverlayRef } from '../../../hooks/useCategoricalBarChartOverlayRef.js';
import { useCategoricalBarChartScales } from '../../../hooks/useCategoricalBarChartScales.js';
import { useCategoricalBarChartThresholds } from '../../../hooks/useCategoricalBarChartThresholds.js';
import { useClosestDimensionInThresholdPoints } from '../../../hooks/useClosestDimensionInThresholdPoints.js';
import { useClosestDimensionPoint } from '../../../hooks/useClosestDimensionPoint.js';
import { useFormatter } from '../../../hooks/useFormatter.js';
import { DimensionXYPoint } from '../../../types/categorical-bar-chart-data.js';
import { isStacked, isVertical } from '../../../utils/layout.js';
import { CategoricalBarChartDatapoints } from '../datapoints/CategoricalBarChartDatapoints.js';
import { CategoricalBarChartTooltip } from '../tooltip/CategoricalBarChartTooltip.js';
import {
  generateGroupDataPoints,
  getGroupDataPointsBy,
  mapToDataPoint,
} from '../utils/data-points.js';

export const CROSSHAIR_BAR_CHART_LABEL = 'crosshair for bar chart';

export const CategoricalBarChartInspectOverlay = () => {
  const { layout, groupMode } = useCategoricalBarChartBaseConfig();
  const { width, height } = useGridPlotSize();
  const { unit } = useCategoricalBarChartData();
  const formatter = useFormatter();
  const { valueScale } = useCategoricalBarChartScales();
  const { visibleTooltip, pinnedTooltip } = useTooltipState();
  const mousePosition = useMousePositionState();
  const thresholds = useCategoricalBarChartThresholds();
  const chartRef = useCategoricalBarChartOverlayRef();
  const isInChartBounds = useMouseInBoundsState();

  const chartBounds = chartRef?.current?.getBoundingClientRect();

  const hasThresholds = thresholds && thresholds.length > 0;

  const [pinnedClosestPoint, setPinnedClosestPoint] =
    useState<DimensionXYPoint>();
  const [pinnedGroupDataPoint, setPinnedGroupDataPoint] =
    useState<GroupDataPoint>();

  const magnifierCoordinate = isVertical(layout) ? 'y' : 'x';

  const magnifierValue =
    mousePosition &&
    getMagnifierLabel(
      unit,
      magnifierCoordinate,
      formatter,
      mousePosition.relative,
      valueScale,
    );
  const isGroupedAllowed = isStacked(groupMode);

  const { closestDimensionPoint, closestDimensionPoints } =
    useClosestDimensionPoint(layout, groupMode, mousePosition?.relative);

  const closestDimensionPointInThreshold = useClosestDimensionInThresholdPoints(
    layout,
    groupMode,
    mousePosition?.relative,
  );

  const groupDataPoints = generateGroupDataPoints(
    closestDimensionPoints,
    isGroupedAllowed,
    layout,
    layout === 'vertical' ? height : width,
  );

  useEffect(() => {
    if (!visibleTooltip && !pinnedTooltip) {
      setPinnedClosestPoint((prevState) =>
        prevState !== undefined ? undefined : prevState,
      );
      setPinnedGroupDataPoint((prevState) =>
        prevState !== undefined ? undefined : prevState,
      );
    }
  }, [visibleTooltip, pinnedTooltip]);

  useEffect(() => {
    if (pinnedTooltip) {
      onClickHandler();
    }
  }, [width, height]);

  const setState = useSetState();

  const onScrollCallback = () => setState(setMouseOutBounds);
  const notShowingMagnifiers = pinnedTooltip || !isInChartBounds;

  useApplyCallbackOnScroll(notShowingMagnifiers, onScrollCallback);

  const onClickHandler = () => {
    setState((prev) => ({
      ...prev,
      overlays: {
        ...prev.overlays,
        pinnedTooltip: setPinned(prev.overlays.pinnedTooltip),
      },
    }));

    setPinnedClosestPoint((prev) =>
      !prev && visibleTooltip
        ? closestDimensionPointInThreshold ?? closestDimensionPoint
        : undefined,
    );
    setPinnedGroupDataPoint((prev) => {
      if (!prev && closestDimensionPoint && visibleTooltip) {
        const dimensionId = (
          closestDimensionPointInThreshold ?? closestDimensionPoint
        )?.datapoint.id;

        return getGroupDataPointsBy(groupDataPoints, dimensionId);
      }

      return undefined;
    });
  };

  const setPinned = (prevPinned: boolean) =>
    visibleTooltip ? !prevPinned : false;

  const verticalCrosshairX =
    pinnedTooltip && pinnedClosestPoint
      ? pinnedClosestPoint.x ?? 0
      : mousePosition?.relative.x ?? 0;
  const verticalCrosshairLabel =
    pinnedTooltip || !mousePosition ? '' : magnifierValue;

  const horizontalCrosshairY =
    pinnedTooltip && pinnedClosestPoint
      ? pinnedClosestPoint.y ?? 0
      : mousePosition?.relative.y ?? 0;
  const horizontalCrosshairLabel =
    pinnedTooltip || !mousePosition ? '' : magnifierValue;

  const closestPoint =
    pinnedTooltip && pinnedClosestPoint
      ? pinnedClosestPoint
      : closestDimensionPointInThreshold;

  const groupPoints = pinnedGroupDataPoint
    ? pinnedGroupDataPoint
    : groupDataPoints;

  const thresholdSize = hasThresholds ? THRESHOLD_BAR_THICKNESS : 0;

  return (
    <div className={plotLayout}>
      {(mousePosition || pinnedTooltip) && (
        <>
          <div className={dataPointLayout}>
            <CategoricalBarChartDatapoints
              points={groupPoints}
              closestPoint={closestPoint && mapToDataPoint(closestPoint)}
            />
          </div>
          <div>
            <CategoricalBarChartTooltip
              closestDimensionPoint={closestPoint}
              hasHeader={groupPoints.length > 1}
              formatter={formatter}
              unit={unit}
            />
          </div>
        </>
      )}
      {pinnedTooltip || isInChartBounds ? (
        <svg
          className={crosshairLayout}
          aria-label={CROSSHAIR_BAR_CHART_LABEL}
          role="graphics-symbol"
          onClick={onClickHandler}
        >
          {!isVertical(layout) ? (
            <VerticalCrosshair
              x={verticalCrosshairX}
              bottomLabel={verticalCrosshairLabel}
              height={height + thresholdSize}
              offset={thresholdSize}
              dashed={!pinnedTooltip}
              chartBounds={chartBounds}
            />
          ) : (
            <HorizontalCrosshair
              width={width + thresholdSize}
              leftLabel={horizontalCrosshairLabel}
              leftOffset={thresholdSize}
              y={horizontalCrosshairY}
              dashed={!pinnedTooltip}
              chartBounds={chartBounds}
            />
          )}
        </svg>
      ) : null}
    </div>
  );
};

CategoricalBarChartInspectOverlay['displayName'] =
  'CategoricalBarChartInspectOverlay';
